

import {
    ExpandMore as ChevronDown,
    ChevronLeft,
    ChevronRight
} from '@mui/icons-material';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormGroup from '@mui/material/FormGroup';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Switch from '@mui/material/Switch';
import Typography from '@mui/material/Typography';

import type { TimelineFilters as ITimelineFilters } from '@/lib/timeline-utils';
import type { EntitySummary, NarrativeStatus } from '@/lib/types';

interface NarrativeFiltersProps {
  open: boolean;
  onClose: () => void;
  filters: ITimelineFilters;
  onFiltersChange: (filters: ITimelineFilters) => void;
  locations: EntitySummary[];
  characters: EntitySummary[];
  organizations: EntitySummary[];
}

export function NarrativeFilters({
  open,
  onClose,
  filters,
  onFiltersChange,
  locations,
  characters,
  organizations,
}: NarrativeFiltersProps) {
  const toggleLocation = (locationId: string) => {
    const newLocationIds = filters.locationIds.includes(locationId)
      ? filters.locationIds.filter((id) => id !== locationId)
      : [...filters.locationIds, locationId];

    onFiltersChange({ ...filters, locationIds: newLocationIds });
  };

  const toggleCharacter = (characterId: string) => {
    const newCharacterIds = filters.characterIds.includes(characterId)
      ? filters.characterIds.filter((id) => id !== characterId)
      : [...filters.characterIds, characterId];

    onFiltersChange({ ...filters, characterIds: newCharacterIds });
  };

  const toggleOrganization = (organizationId: string) => {
    const newOrganizationIds = filters.organizationIds.includes(organizationId)
      ? filters.organizationIds.filter((id) => id !== organizationId)
      : [...filters.organizationIds, organizationId];

    onFiltersChange({ ...filters, organizationIds: newOrganizationIds });
  };

  const toggleNarrativeStatus = (status: NarrativeStatus) => {
    const newStatus = filters.narrativeStatus.includes(status)
      ? filters.narrativeStatus.filter((s) => s !== status)
      : [...filters.narrativeStatus, status];

    onFiltersChange({ ...filters, narrativeStatus: newStatus });
  };

  return (
    <Paper
      elevation={0}
      sx={{
        width: open ? 320 : 60,
        transition: 'width 0.3s ease-in-out',
        borderLeft: 1,
        borderColor: 'divider',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        bgcolor: 'background.paper',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          p: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: open ? 'space-between' : 'center',
          minHeight: 64,
        }}
      >
        {open && (
          <Typography variant="h6" sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>
            Filters
          </Typography>
        )}
        <IconButton onClick={onClose} size="small">
          {open ? <ChevronRight sx={{ fontSize: 20 }} /> : <ChevronLeft sx={{ fontSize: 20 }} />}
        </IconButton>
      </Box>

      {/* Filter Content - only show when expanded */}
      {open && (
        <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
          {/* Locations */}
          <Accordion defaultExpanded>
            <AccordionSummary expandIcon={<ChevronDown sx={{ fontSize: 20 }} />}>
              <Typography variant="subtitle2">Locations</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <FormControlLabel
                control={
                  <Switch
                    checked={filters.includeSublocations}
                    onChange={(e) =>
                      onFiltersChange({ ...filters, includeSublocations: e.target.checked })
                    }
                    size="small"
                  />
                }
                label={<Typography variant="caption">Include sublocations</Typography>}
                sx={{ mb: 1 }}
              />
              <FormGroup>
                {locations.slice(0, 10).map((location) => (
                  <FormControlLabel
                    key={location.id}
                    control={
                      <Checkbox
                        checked={filters.locationIds.includes(location.id)}
                        onChange={() => toggleLocation(location.id)}
                        size="small"
                      />
                    }
                    label={<Typography variant="body2">{location.name}</Typography>}
                  />
                ))}
              </FormGroup>
            </AccordionDetails>
          </Accordion>

          {/* Characters */}
          <Accordion>
            <AccordionSummary expandIcon={<ChevronDown sx={{ fontSize: 20 }} />}>
              <Typography variant="subtitle2">Characters</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <FormGroup>
                {characters.slice(0, 10).map((character) => (
                  <FormControlLabel
                    key={character.id}
                    control={
                      <Checkbox
                        checked={filters.characterIds.includes(character.id)}
                        onChange={() => toggleCharacter(character.id)}
                        size="small"
                      />
                    }
                    label={<Typography variant="body2">{character.name}</Typography>}
                  />
                ))}
              </FormGroup>
            </AccordionDetails>
          </Accordion>

          {/* Organizations */}
          <Accordion>
            <AccordionSummary expandIcon={<ChevronDown sx={{ fontSize: 20 }} />}>
              <Typography variant="subtitle2">Organizations</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <FormGroup>
                {organizations.slice(0, 10).map((organization) => (
                  <FormControlLabel
                    key={organization.id}
                    control={
                      <Checkbox
                        checked={filters.organizationIds.includes(organization.id)}
                        onChange={() => toggleOrganization(organization.id)}
                        size="small"
                      />
                    }
                    label={<Typography variant="body2">{organization.name}</Typography>}
                  />
                ))}
              </FormGroup>
            </AccordionDetails>
          </Accordion>

          {/* Narrative Status */}
          <Accordion>
            <AccordionSummary expandIcon={<ChevronDown sx={{ fontSize: 20 }} />}>
              <Typography variant="subtitle2">Narrative Status</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <FormGroup>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={filters.narrativeStatus.includes('notWritten')}
                      onChange={() => toggleNarrativeStatus('notWritten')}
                      size="small"
                    />
                  }
                  label={<Typography variant="body2">Not Written</Typography>}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={filters.narrativeStatus.includes('partial')}
                      onChange={() => toggleNarrativeStatus('partial')}
                      size="small"
                    />
                  }
                  label={<Typography variant="body2">Partial</Typography>}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={filters.narrativeStatus.includes('complete')}
                      onChange={() => toggleNarrativeStatus('complete')}
                      size="small"
                    />
                  }
                  label={<Typography variant="body2">Complete</Typography>}
                />
              </FormGroup>
            </AccordionDetails>
          </Accordion>
        </Box>
      )}
    </Paper>
  );
}
